import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Card,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import { ArrowForward, ArrowRight, Info } from "@mui/icons-material";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { listUsers, getPricelist, getUser } from "../../graphql/queries";
import { AppContext } from "../../App";
import { useUser } from "../../UserContext";
import {
  getCustomerOffsets,
  applyOffsets,
} from "../../utils/pricelistPushUtils";
import InnerPage from "../InnerPage";

const SuperAdminOffsetSearch = () => {
  const { userRecord } = useContext(AppContext);
  const { userClass } = useUser();
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offsetChain, setOffsetChain] = useState([]);
  const [selectedPricelist, setSelectedPricelist] = useState(null);
  const [pricelists, setPricelists] = useState([]);

  // Fetch providers (users who are providers)
  const fetchProviders = async () => {
    try {
      const response = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { isProvider: { eq: true } },
          limit: 1000,
        }),
      );
      const providerList = (response.data.listUsers.items || []).filter(
        (u) => u.id !== userRecord.id,
      );
      setProviders(providerList);
    } catch (error) {
      console.error("Error fetching providers:", error);
    }
  };

  // Fetch customers of selected provider
  const fetchCustomers = async (providerId) => {
    try {
      const response = await API.graphql(
        graphqlOperation(listUsers, {
          filter: { createdByUserId: { eq: providerId } },
          limit: 1000,
        }),
      );
      const customerList = response.data.listUsers.items || [];
      setCustomers(customerList);
      setSelectedCustomer(null);
      setSelectedPricelist(null);
      setSearchResults([]);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  // Fetch pricelists for selected customer
  const fetchPricelists = async (userId) => {
    try {
      const userResponse = await API.graphql(
        graphqlOperation(getUser, { id: userId }),
      );
      const userData = userResponse.data.getUser;

      if (userData.activePricelistID) {
        const pricelistResponse = await API.graphql(
          graphqlOperation(getPricelist, { id: userData.activePricelistID }),
        );
        setSelectedPricelist(pricelistResponse.data.getPricelist);
      } else {
        setSelectedPricelist(null);
      }
      setPricelists([userData.activePricelist].filter(Boolean));
    } catch (error) {
      console.error("Error fetching pricelists:", error);
    }
  };

  // Get offset chain for customer (including parent offsets)
  const getOffsetChain = async (customerId) => {
    const chain = [];
    let currentId = customerId;
    const visited = new Set();
    const customerMap = new Map();

    // Build customer map
    const allCustomers = [...customers];
    if (selectedProvider) {
      allCustomers.push(selectedProvider);
    }
    allCustomers.forEach((c) => customerMap.set(c.id, c));

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const customer = customerMap.get(currentId);
      if (customer) {
        chain.push(customer);
        currentId = customer.createdByUserId; // Move up to parent
      } else {
        break;
      }
    }

    // Get offsets for each level
    const chainWithOffsets = await Promise.all(
      chain.map(async (customer) => {
        const offsets = await getCustomerOffsets(customer);
        return {
          ...customer,
          offsets,
        };
      }),
    );

    setOffsetChain(chainWithOffsets);
    return chainWithOffsets;
  };

  // Search parts with offset simulation
  const handleSearch = async () => {
    if (!selectedCustomer || !selectedPricelist) {
      alert("Please select a provider, customer, and pricelist");
      return;
    }

    setLoading(true);
    try {
      // Get the offset chain
      await getOffsetChain(selectedCustomer.id);

      // Fetch pricelist data from S3
      const pricelistDataResponse = await Storage.get(selectedPricelist.s3Key, {
        download: true,
      });
      const pricelistData = JSON.parse(await pricelistDataResponse.Body.text());

      // Filter and apply offsets
      const results = pricelistData.filter(
        (item) =>
          item["PART NUMBER"] &&
          item["PART NUMBER"]
            .toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );

      // Apply stacked offsets to each result
      const adjustedResults = results.map((item) => {
        const basePrice =
          item.adjustedPrice !== undefined ? item.adjustedPrice : item.PRICE;
        let finalPrice = basePrice;
        // Apply offsets in order
        for (const customer of offsetChain) {
          finalPrice = applyOffsets(finalPrice, customer.offsets);
        }
        return {
          ...item,
          basePrice,
          finalPrice,
          adjustedByOffsets: finalPrice !== basePrice,
        };
      });

      setSearchResults(adjustedResults);
    } catch (error) {
      console.error("Error searching with offsets:", error);
      alert("Error searching: " + error.message);
    } finally {
      setLoading(false);
    }
  };
  const formatOffsetDisplay = (offsets) => {
    const parts = [];
    if (offsets.percentageAdjustment !== 0) {
      parts.push(
        `${offsets.percentageAdjustment > 0 ? "+" : ""}${offsets.percentageAdjustment}%`,
      );
    }
    if (offsets.dollarAdjustment !== 0) {
      parts.push(
        `${offsets.dollarAdjustment > 0 ? "+" : ""}$${offsets.dollarAdjustment}`,
      );
    }
    return parts.length > 0 ? parts.join(" + ") : "None";
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    if (selectedProvider) {
      fetchCustomers(selectedProvider.id);
      setSelectedCustomer(null);
      setSelectedPricelist(null);
      setSearchResults([]);
    }
  }, [selectedProvider]);

  useEffect(() => {
    if (selectedCustomer) {
      fetchPricelists(selectedCustomer.id);
      setSearchResults([]);
    }
  }, [selectedCustomer]);

  if (userClass !== "SuperAdmin") {
    return null;
  }

  return (
    <InnerPage>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          SuperAdmin Offset Search
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Search a provider's customer pricelist with all offsets applied as if
          you were viewing it from that customer's perspective.
        </Typography>

        {/* Provider Selection */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 1: Select Provider
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Provider</InputLabel>
            <Select
              value={selectedProvider?.id || ""}
              onChange={(e) => {
                const provider = providers.find((p) => p.id === e.target.value);
                setSelectedProvider(provider || null);
              }}
              label="Provider"
            >
              {providers.map((provider) => (
                <MenuItem key={provider.id} value={provider.id}>
                  {provider.username || provider.email} -{" "}
                  {provider.companyName || "N/A"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Card>

        {/* Customer Selection */}
        {selectedProvider && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Step 2: Select Customer
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Customer</InputLabel>
              <Select
                value={selectedCustomer?.id || ""}
                onChange={(e) => {
                  const customer = customers.find(
                    (c) => c.id === e.target.value,
                  );
                  setSelectedCustomer(customer || null);
                }}
                label="Customer"
                disabled={customers.length === 0}
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.username || customer.email} -{" "}
                    {customer.companyName || "N/A"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Card>
        )}

        {/* Offset Chain Display */}
        {selectedCustomer && offsetChain.length > 0 && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Offset Chain (From Provider to Customer)
            </Typography>
            <List>
              {offsetChain.map((customer, index) => (
                <ListItem key={customer.id} sx={{ py: 1 }}>
                  <ListItemText
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {index === 0
                          ? "Provider"
                          : index === offsetChain.length - 1
                            ? "Customer"
                            : "Intermediate"}
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                          {customer.username || customer.email}
                        </Typography>
                        <Chip
                          label={formatOffsetDisplay(customer.offsets)}
                          size="small"
                          color={
                            customer.offsets.percentageAdjustment !== 0 ||
                            customer.offsets.dollarAdjustment !== 0
                              ? "primary"
                              : "default"
                          }
                        />
                      </Box>
                    }
                    secondary={
                      index < offsetChain.length - 1 ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <ArrowForward fontSize="small" color="disabled" />
                          <Typography variant="caption">
                            → Next level
                          </Typography>
                        </Box>
                      ) : null
                    }
                  />
                  {index < offsetChain.length - 1 && (
                    <ArrowRight color="disabled" />
                  )}
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 2 }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <Info
                  fontSize="small"
                  sx={{ mr: 1, verticalAlign: "middle" }}
                />
                Prices will be adjusted according to all offsets in the chain
              </Typography>
            </Box>
          </Card>
        )}

        {/* Search Input */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Step 3: Search Parts
          </Typography>
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              fullWidth
              label="Part Number"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading || !searchTerm || !selectedCustomer}
              size="large"
            >
              {loading ? <CircularProgress size={24} /> : "Search"}
            </Button>
          </Box>
          {selectedPricelist && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              Using pricelist: {selectedPricelist.name}
            </Typography>
          )}
        </Card>

        {/* Search Results */}
        {searchResults.length > 0 && (
          <Card sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Search Results ({searchResults.length} parts)
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Part Number</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Base Price</TableCell>
                    <TableCell align="right">Final Price</TableCell>
                    <TableCell align="right">Adjustment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {searchResults.map((part) => (
                    <TableRow key={part.id || part["PART NUMBER"]} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: "bold" }}>
                          {part["PART NUMBER"]}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {part["DESCRIPTION"] || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography>
                          ${Math.round(part.basePrice || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography
                          sx={{
                            color:
                              part.adjustedByOffsets &&
                              (part.finalPrice > part.basePrice
                                ? "success.main"
                                : "error.main"),
                            fontWeight: "bold",
                          }}
                        >
                          ${Math.round(part.finalPrice || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {part.adjustedByOffsets ? (
                          <Chip
                            label={`$${Math.round(part.finalPrice - part.basePrice)}`}
                            size="small"
                            color={
                              part.finalPrice > part.basePrice
                                ? "success"
                                : "error"
                            }
                          />
                        ) : (
                          <Typography color="text.secondary">
                            No change
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}
      </Box>
    </InnerPage>
  );
};

export default SuperAdminOffsetSearch;
